export interface CompanyPeopleSnapGeometry {
  start: number;
  viewport: number;
  steps: number;
}

/** One deliberate wheel gesture per stage; ignore the gesture's inertial tail. */
export class CompanyPeopleHeroSnap {
  private frame = 0;
  private idleTimer = 0;
  private moving = false;
  private armed = true;
  private lastWheel = 0;
  private lockedUntil = 0;
  private budget = 0;
  private direction = 0;
  private touching = false;
  private touchStartTop = 0;
  private touchEligible = false;

  constructor(
    private host: Window | HTMLElement,
    private geometry: () => CompanyPeopleSnapGeometry | undefined,
    private onMoving: (moving: boolean) => void,
  ) {}

  connect() {
    this.host.addEventListener('wheel', this.onWheel as EventListener, { passive: false });
    this.host.addEventListener('scroll', this.onScroll, { passive: true });
    this.host.addEventListener('pointerdown', this.reset);
    this.host.addEventListener('touchstart', this.onTouchStart as EventListener, { passive: true });
    this.host.addEventListener('touchend', this.onTouchEnd as EventListener, { passive: true });
    this.host.addEventListener('touchcancel', this.onTouchEnd as EventListener, { passive: true });
    this.host.addEventListener('close', this.onDialogClose, { capture: true });
    window.addEventListener('keydown', this.onKey);
    window.addEventListener('resize', this.reset);
  }

  disconnect() {
    this.host.removeEventListener('wheel', this.onWheel as EventListener);
    this.host.removeEventListener('scroll', this.onScroll);
    this.host.removeEventListener('pointerdown', this.reset);
    this.host.removeEventListener('touchstart', this.onTouchStart as EventListener);
    this.host.removeEventListener('touchend', this.onTouchEnd as EventListener);
    this.host.removeEventListener('touchcancel', this.onTouchEnd as EventListener);
    this.host.removeEventListener('close', this.onDialogClose, { capture: true });
    window.removeEventListener('keydown', this.onKey);
    window.removeEventListener('resize', this.reset);
    this.reset();
  }

  reset = () => {
    cancelAnimationFrame(this.frame);
    window.clearTimeout(this.idleTimer);
    this.frame = 0;
    this.moving = false;
    this.armed = true;
    this.budget = 0;
    this.direction = 0;
    this.lastWheel = 0;
    this.lockedUntil = 0;
    this.onMoving(false);
  };

  private top() {
    return this.host instanceof HTMLElement ? this.host.scrollTop : window.scrollY;
  }

  private nativeControl(target: EventTarget | null) {
    if (!(target instanceof Element)) return false;
    if (
      target.closest(
        'dialog, input, textarea, select, [contenteditable="true"], [role="slider"], [role="menu"], [role="listbox"]',
      )
    )
      return true;
    // An independently scrollable child must keep its own scrolling behavior.
    for (
      let node: Element | null = target;
      node && node !== this.host && node !== document.body;
      node = node.parentElement
    ) {
      if (
        node instanceof HTMLElement &&
        node.scrollHeight > node.clientHeight + 2 &&
        /(auto|scroll)/u.test(getComputedStyle(node).overflowY)
      )
        return true;
    }
    return false;
  }

  private inStages(geometry: CompanyPeopleSnapGeometry) {
    const top = this.top();
    // An incomplete departure still belongs to the nearest (last) stage.
    const tolerance = geometry.viewport * 0.49;
    return top >= geometry.start - 2 && top <= geometry.start + (geometry.steps - 1) * geometry.viewport + tolerance;
  }

  private onDialogClose = (event: Event) => {
    if (!(event.target instanceof HTMLDialogElement)) return;
    const geometry = this.geometry();
    if (!geometry || !this.inStages(geometry)) return;
    // Cancel the browser's smooth focus-restoration scroll beneath a closing dialog.
    const step = Math.max(
      0,
      Math.min(geometry.steps - 1, Math.round((this.top() - geometry.start) / geometry.viewport)),
    );
    this.reset();
    this.host.scrollTo({ top: geometry.start + step * geometry.viewport, behavior: 'instant' });
  };

  private onWheel = (event: WheelEvent) => {
    const geometry = this.geometry();
    if (
      !geometry ||
      !this.inStages(geometry) ||
      event.ctrlKey ||
      event.metaKey ||
      event.shiftKey ||
      Math.abs(event.deltaX) > Math.abs(event.deltaY) ||
      !event.deltaY ||
      this.nativeControl(event.target)
    )
      return;
    const direction = Math.sign(event.deltaY);
    if (direction < 0 && this.top() <= geometry.start + 2) return;
    if (!event.cancelable) return;
    event.preventDefault();
    const now = performance.now();
    const gap = now - this.lastWheel;
    const freshGesture = gap > 170;
    this.lastWheel = now;
    if (this.moving || now < this.lockedUntil) return;
    if (!this.armed) {
      if (!freshGesture) return;
      this.armed = true;
    }
    // Separate mouse-wheel notches can be deliberate even with a short pause.
    if (gap > 650 || this.direction !== direction) this.budget = 0;
    this.direction = direction;
    const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? geometry.viewport : 1;
    this.budget += Math.abs(event.deltaY) * unit;
    const threshold = Math.min(220, Math.max(120, geometry.viewport * 0.12));
    if (this.budget < threshold) return;
    const step = Math.round((this.top() - geometry.start) / geometry.viewport);
    // The final forward step releases the hero into the following page content.
    this.animateTo(geometry.start + Math.max(0, Math.min(geometry.steps, step + direction)) * geometry.viewport);
  };

  private onKey = (event: KeyboardEvent) => {
    if (['Escape', 'Home', 'End'].includes(event.key)) {
      this.reset();
      return;
    }
    const geometry = this.geometry();
    if (
      !geometry ||
      !this.inStages(geometry) ||
      event.ctrlKey ||
      event.metaKey ||
      event.altKey ||
      this.nativeControl(event.target)
    )
      return;
    const direction =
      ['ArrowDown', 'PageDown'].includes(event.key) || (event.key === ' ' && !event.shiftKey)
        ? 1
        : ['ArrowUp', 'PageUp'].includes(event.key) || (event.key === ' ' && event.shiftKey)
          ? -1
          : 0;
    if (!direction) return;
    // Space on a link or button belongs to that control.
    if (event.key === ' ' && event.target instanceof Element && event.target.closest('button, a')) return;
    const step = Math.round((this.top() - geometry.start) / geometry.viewport);
    if (step === 0 && direction < 0) return;
    event.preventDefault();
    if (this.moving || event.repeat) return;
    this.animateTo(geometry.start + Math.max(0, Math.min(geometry.steps, step + direction)) * geometry.viewport);
  };

  private onScroll = () => {
    if (this.moving || this.touching || (window.visualViewport?.scale ?? 1) > 1) return;
    window.clearTimeout(this.idleTimer);
    this.idleTimer = window.setTimeout(() => {
      const geometry = this.geometry();
      if (!geometry || !this.inStages(geometry)) return;
      const step = Math.round((this.top() - geometry.start) / geometry.viewport);
      const target = geometry.start + step * geometry.viewport;
      if (Math.abs(this.top() - target) > 2) this.animateTo(target);
    }, 220);
  };

  private onTouchStart = (event: TouchEvent) => {
    this.reset();
    this.touching = true;
    const geometry = this.geometry();
    this.touchStartTop = this.top();
    this.touchEligible = event.touches.length === 1 && !this.nativeControl(event.target) &&
      Boolean(geometry && this.inStages(geometry));
  };

  private onTouchEnd = (event: TouchEvent) => {
    if (event.touches.length) return;
    this.touching = false;
    const geometry = this.geometry();
    if (!this.touchEligible || !geometry || event.type === 'touchcancel' || (window.visualViewport?.scale ?? 1) > 1)
      return;
    const delta = this.top() - this.touchStartTop;
    if (Math.abs(delta) < 2) return;
    const start = Math.round((this.touchStartTop - geometry.start) / geometry.viewport);
    const deliberate = Math.abs(delta) >= Math.max(32, geometry.viewport * 0.08);
    const step = Math.max(0, Math.min(geometry.steps, start + (deliberate ? Math.sign(delta) : 0)));
    // Keep the drag native, then cancel its inertial tail with one deliberate stage.
    this.animateTo(geometry.start + step * geometry.viewport, 500);
  };

  private animateTo(target: number, duration = 680) {
    cancelAnimationFrame(this.frame);
    window.clearTimeout(this.idleTimer);
    const from = this.top();
    const start = performance.now();
    this.moving = true;
    this.armed = false;
    this.budget = 0;
    this.onMoving(true);
    const tick = (time: number) => {
      const progress = Math.max(0, Math.min(1, (time - start) / duration));
      // Continuous acceleration/deceleration gives the portrait springs time to yield.
      const ease = progress ** 3 * (progress * (progress * 6 - 15) + 10);
      this.host.scrollTo({ top: from + (target - from) * ease, behavior: 'instant' });
      if (progress < 1) this.frame = requestAnimationFrame(tick);
      else {
        this.frame = 0;
        this.moving = false;
        this.lockedUntil = performance.now() + 180;
        this.onMoving(false);
      }
    };
    this.frame = requestAnimationFrame(tick);
  }
}
